import { useId } from 'react';
import type { InputHTMLAttributes } from 'react';

import { TextInput } from './form/TextInput';

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  error?: string;
  hint?: string;
}

/**
 * A labelled input with its error message.
 *
 * The wiring is the point. `useId` ties the label to the control so clicking
 * the label focuses it, and `aria-describedby` ties the error to the control so
 * a screen reader announces it on focus rather than leaving it as red text
 * floating nearby. `aria-invalid` is what makes the field announce as invalid
 * at all.
 *
 * Doing this per-form is how it gets skipped, which is why there is a component.
 */
export function Field({ label, error, hint, className = '', ...rest }: Props) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-soft">
        {label}
      </label>

      {/*
        The styling comes from the kit, not from here.
 
        This used to carry its own copy of the border, padding and focus
        colours, which is how a labelled field and a bare one ended up looking
        subtly different. `TextInput` is the one definition now; this component
        is the label, the hint and the wiring between them.
      */}
      <TextInput
        id={id}
        invalid={Boolean(error)}
        aria-describedby={describedBy || undefined}
        className={className}
        {...rest}
      />

      {hint && !error && (
        <p id={hintId} className="text-xs text-faint">
          {hint}
        </p>
      )}

      {error && (
        <p id={errorId} className="text-xs text-negative">
          {error}
        </p>
      )}
    </div>
  );
}
